import type { Level, LevelBound, LevelState } from './levelHelpers'
import { formatAmount } from './levelHelpers'
import { LEVEL_ICON_COMPONENTS } from './levelIcons'
import LevelPopover from './LevelPopover'
import type { LevelPopoverOpenHandler, LevelTrackerLabels } from './levelTrackerTypes'

interface LevelTrackProps {
  state: LevelState
  levels: Level[]
  received: number
  labels: LevelTrackerLabels
  onPopoverOpen: LevelPopoverOpenHandler
}

const FILLED_CIRCLE = 'bg-success text-success-txt shadow-lg shadow-success/30 size-14 md:size-18 [&_svg]:size-6 md:[&_svg]:size-8'
const EMPTY_CIRCLE = 'bg-alt border-2 border-pending text-pending-txt size-12 md:size-15 [&_svg]:size-5 md:[&_svg]:size-6.5'

function CircleIcon({ bound }: { bound: LevelBound }) {
  const Icon = LEVEL_ICON_COMPONENTS[bound.icon]
  return <Icon aria-hidden='true' />
}

/**
 * The zoomed-in track: the current bracket only, from the left circle (level
 * just cleared, or $0) to the right circle (next level), with faded stubs
 * hinting at cleared track on the left and more levels on the right.
 */
export default function LevelTrack({ state, levels, received, labels, onPopoverOpen }: LevelTrackProps) {
  const { current, next, done, pct, showLeftStub, showRightStub } = state
  const nextLevel = levels[next.level - 1]
  const leftFilled = received > 0

  return (
    <div className='flex items-center pb-10 md:pb-11'>
      {showLeftStub && (
        <div
          aria-hidden='true'
          className='from-success -mr-7 h-3.5 w-16 shrink-0 rounded-full bg-linear-to-l from-45% to-transparent opacity-50 md:-mr-9 md:w-25.5'
        />
      )}

      {/* Left circle: not interactive. */}
      <div className='relative z-10 grid size-14 shrink-0 place-items-center md:size-18'>
        <div
          aria-hidden='true'
          className={`grid place-items-center rounded-full ${leftFilled ? FILLED_CIRCLE : EMPTY_CIRCLE}`}
        >
          <CircleIcon bound={current} />
        </div>
        <span
          className={`text-alt-txt absolute top-full mt-3 text-base font-bold whitespace-nowrap md:text-lg ${current.level === 0 ? 'invisible' : ''}`}
        >
          {formatAmount(current.amount)}
        </span>
      </div>

      <div aria-hidden='true' className='bg-track relative -mx-7 h-3.5 flex-1 rounded-full md:-mx-9'>
        <div
          className='bg-success h-full rounded-full transition-[width] duration-600 ease-out'
          style={{ width: `${pct}%` }}
        />
      </div>

      {/* Right circle: opens the next level's popover. */}
      <div className='relative z-10 grid size-14 shrink-0 place-items-center md:size-18'>
        <LevelPopover
          title={labels.levelTitle(next.level, nextLevel.title)}
          description={nextLevel.description}
          ariaLabel={labels.levelTitle(next.level, nextLevel.title)}
          align='end'
          triggerClassName={`focus-visible:ring-cta grid cursor-pointer place-items-center rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${done ? FILLED_CIRCLE : EMPTY_CIRCLE}`}
          onOpen={(method) => onPopoverOpen(next.level, 'next_circle', method)}
        >
          <CircleIcon bound={next} />
        </LevelPopover>
        <span
          className={`absolute top-full mt-3 text-base font-bold whitespace-nowrap md:text-lg ${done ? 'text-alt-txt' : 'text-secondary-txt'}`}
        >
          {formatAmount(next.amount)}
        </span>
      </div>

      {showRightStub && (
        <div
          aria-hidden='true'
          className='from-track -ml-7 h-3.5 w-16 shrink-0 rounded-full bg-linear-to-r from-45% to-transparent opacity-50 md:-ml-9 md:w-25.5'
        />
      )}
    </div>
  )
}
